import styled from 'styled-components';

import { CustomThemeToken } from '@/theme';

import { getWidgetFillColor } from '../../utils/utils';

const RIGHT_BORDER_WIDTH_DETAIL = '110px';
const RIGHT_BORDER_WIDTH_MAP = '68.15px';

interface ContainerProps {
  width: number;
  height: number;
  $mapView: number;
  $isSelected: boolean;
  $isInMaintenance: boolean;
  $poweredOnState: boolean;
  theme: { token: CustomThemeToken };
}

export const Container = styled.div<ContainerProps>`
  position: relative;
  width: ${({ width }) => width}px;
  height: ${({ height }) => height}px;
  padding: 0 ${({ theme: { token } }) => token.paddingXXS}px 0 0;
  display: flex;
  flex-direction: column;
  align-items: center;

  .background {
    position: absolute;
    top: 0;
    right: 0;
    z-index: ${({ $mapView }) => $mapView};
    height: 100%;
  }

  .toggles-wrapper {
    width: 100%;
    height: 52px;
    z-index: 3;
    position: absolute;
    display: none;
    justify-content: flex-end;
    bottom: 0;
    left: 0;
  }

  .toggle-status-box {
    min-width: ${({ $mapView }) => ($mapView ? RIGHT_BORDER_WIDTH_MAP : RIGHT_BORDER_WIDTH_DETAIL)};
  }

  .top-row {
    width: 100%;
    position: relative;
    height: 35%;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;

    .control-title-wrapper {
      display: flex;
      height: 100%;
      width: 70%;
      justify-content: flex-start;
      margin-left: ${({ $mapView, theme: { token } }) => ($mapView ? token.marginXS : token.margin)}px;
      align-items: center;

      .title {
        margin-left: ${({ theme: { token } }) => token.marginSM}px;
        text-transform: capitalize;
      }

      .model {
        color: ${({ theme: { token } }) => token.colorTextSecondary};
        font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
      }
    }

    .connectivity-icon-wrapper {
      margin-right: ${({ $mapView, theme: { token } }) => ($mapView ? token.marginMD : token.marginXL)}px;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      align-content: center;
    }
  }

  .upper-right-border,
  .lower-right-border {
    width: 1px;
    right: ${({ $mapView }) => ($mapView ? RIGHT_BORDER_WIDTH_MAP : RIGHT_BORDER_WIDTH_DETAIL)};
    background-color: ${({ theme: { token } }) => token.colorBgBase};
    position: absolute;
    z-index: 1;
  }

  .upper-right-border {
    top: 0;
    height: 35%;
    bottom: 20.7%;
  }

  .lower-right-border {
    height: 100%;
    bottom: 0;
  }

  .status-widget-wrapper {
    position: absolute;
    inset: -24px 4px -1px 0px;
    display: ${({ $isInMaintenance, $poweredOnState }) => ($isInMaintenance || !$poweredOnState ? 'flex' : 'none')};
    flex-direction: column;
    z-index: 4;

    header {
      display: flex;
      align-items: center;
      padding: 0 ${({ theme: { token } }) => token.paddingSM}pxs;
      background-color: ${({ $poweredOnState, theme: { token } }) =>
        !$poweredOnState ? token.Ecoplant_Compressor.colorBGStopped : token.Ecoplant_Compressor.colorBGMaintenance};
      border: solid 1px
        ${({ $poweredOnState, theme: { token } }) =>
          !$poweredOnState ? token.Ecoplant_Compressor.colorBGStopped : token.Ecoplant_Compressor.colorBGMaintenance};
      border-radius: 2px 2px 0 0;
      height: 24px;

      .title {
        font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
        color: ${({ $poweredOnState, theme: { token } }) =>
          !$poweredOnState ? token.colorErrorText : token.colorWarningTextHover};
        text-transform: capitalize;
      }
    }

    main {
      flex-grow: 1;
      border: 1px solid
        ${({ $poweredOnState, theme: { token } }) =>
          !$poweredOnState ? token.Ecoplant_Compressor.colorBGStopped : token.Ecoplant_Compressor.colorBGMaintenance};
    }
  }

  > header {
    display: flex;
    align-items: center;
    padding: 0 ${({ theme: { token } }) => token.paddingXL}px 0 ${({ theme: { token } }) => token.paddingXS}px;
    width: 100%;
    height: 50px;

    .name {
      font-size: ${({ theme: { token } }) => token.fontSize}px;
    }

    .model {
      font-size: ${({ theme: { token } }) => token.fontSize}px;
      color: ${({ theme: { token } }) => token.colorTextSecondary};
    }
  }

  section {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 90px;
    background-color: ${({ $isSelected, theme: { token } }) => getWidgetFillColor($isSelected, token)};
    padding: ${({ theme: { token } }) => token.paddingXXS}px;
    position: relative;
    z-index: 2;
    width: 98%;
    top: 1px;

    .metrics-list {
      display: flex;
      width: 90%;
      z-index: 2;
      justify-content: center;

      .metrics-list-item {
        padding-right: ${({ theme: { token } }) => token.paddingXS}px;

        .highlighted {
          color: ${({ theme: { token } }) => token.colorText};
        }

        .metrics-list-item-value {
          font-size: ${({ theme: { token } }) => token.fontSizeXL}px;
          font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
          color: ${({ theme: { token } }) => token.colorTextTertiary};

          &.highlighted {
            color: ${({ theme: { token } }) => token.colorText};
          }
        }

        .metrics-list-item-label {
          font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
          margin-left: ${({ theme: { token } }) => token.marginXXS}px;
          color: ${({ theme: { token } }) => token.colorTextSecondary};
        }
      }
    }
  }
`;
